import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  Icon,
  Link,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Tooltip,
  List,
  ListItem,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { WipConfig } from '../../types';
import { CsvDownloadButton } from 'packages/common-frontend/components/CsvDownloadButton';
import {
  AddIconButton,
  EditIconButton,
} from 'packages/common-frontend/components/IconButton';
import { StickyHeaderSection } from 'packages/common-frontend/components/StickyHeaderSection';
import { AddDeliverableDueDateModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/add/AddDeliverableDueDateModal';
import {
  getBooleanSymbol,
  getIterationKey,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteArrayPropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { EditButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditButton';
import { EditReportingDeliverableModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditReportingDeliverableModal';
import { EditReportingDeliverablesPermissionsModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditReportingDeliverablesPermissionsModal';
import { FiInfo } from 'react-icons/fi';

const metadataHeaders: (
  | keyof CreditAgreement.DomainEntity.DEPRECATED.DeliverablePropertyWithEntities
  | keyof CreditAgreement.DomainEntity.DEPRECATED.DeliverablePropertyWithEntities['_config_']
)[] = [
  'name',
  'description',
  'section',
  'deliverableType',
  'frequency',
  'responsibleParties',
  '_isWrittenIn_',
];

type PermissionRowProps = {
  entity: CreditAgreement.DomainEntity.Items.BaseOrganizationEntity['entity'];
  canView: boolean;
  canDownload: boolean;
  wipConfig?: WipConfig;
  deliverableName: string;
  deliverablePermission: CreditAgreement.Config.DeliverablePropertyDEPRECATED.PartyPermissionWithEntity;
  partyPermissions: CreditAgreement.Config.DeliverablePropertyDEPRECATED.PartyPermissionWithEntity[];
  fullPropertyKey: string;
  permissionIdx: number;
};
const PermissionRow = ({
  entity,
  canView,
  canDownload,
  wipConfig,
  deliverableName,
  deliverablePermission,
  partyPermissions,
  fullPropertyKey,
  permissionIdx,
}: PermissionRowProps) => {
  const [isEditPermissionOpen, setIsEditPermissionOpen] = useState<boolean>(
    false,
  );
  return (
    <Tr key={entity.id}>
      <Td>{entity.name}</Td>
      <Td textAlign="center">{getBooleanSymbol(canView)}</Td>
      <Td textAlign="center">{getBooleanSymbol(canDownload)}</Td>
      {wipConfig?.isMutateAllowed(
        `${fullPropertyKey}._config_.partyPermissions`,
      ) && (
        <Td>
          <EditIconButton
            ariaLabel="add permissions"
            label="Add permissions"
            onClick={() => setIsEditPermissionOpen(true)}
          />
          <EditReportingDeliverablesPermissionsModal
            deliverableName={deliverableName}
            deliverablePermission={deliverablePermission}
            partyPermissions={partyPermissions}
            isOpen={isEditPermissionOpen}
            setIsOpen={setIsEditPermissionOpen}
            onSubmit={(newPermissions) => {
              wipConfig.formProps.setFieldValue(
                `${fullPropertyKey}._config_.partyPermissions`,
                newPermissions,
              );
            }}
            wipConfig={wipConfig}
          />
          <DeleteArrayPropertyButton
            basePropertyKey={`${fullPropertyKey}._config_.partyPermissions`}
            wipConfig={wipConfig}
            idx={permissionIdx}
          />
        </Td>
      )}
    </Tr>
  );
};

type ReportingDeliverableProps = {
  basePropertyKey: string;
  idx: number;
  reportingDeliverable: CreditAgreement.DomainEntity.DEPRECATED.DeliverablePropertyWithEntities;
  wipConfig?: WipConfig;
};
const ReportingDeliverable = ({
  basePropertyKey,
  idx,
  reportingDeliverable,
  wipConfig,
}: ReportingDeliverableProps) => {
  const [editIsOpen, setEditIsOpen] = useState<boolean>(false);
  const [isEditPermissionOpen, setIsEditPermissionOpen] = useState<boolean>(
    false,
  );
  const [uploadDueDatesIsOpen, setUploadDueDatesIsOpen] = useState<boolean>(
    false,
  );
  const fullPropertyKey = `${basePropertyKey}[${idx}]`;

  const {
    name,
    description,
    section,
    responsibleParties,
    _isWrittenIn_: isWrittenIn,
    _config_: {
      deliverableType,
      frequency,
      dependencies,
      partyPermissions,
      configKey,
      preUploadNotificationProcesses,
      postUploadNotificationProcesses,
    },
  } = reportingDeliverable;

  return (
    <Box mb="40px" mt="20px" width="100%" id={_.kebabCase(configKey)}>
      <Flex alignItems="center" mb="20px">
        <Text fontWeight="bold" fontSize="20px">
          {`${commonUtils.toTitleCase(
            reportingDeliverable._config_.deliverableType,
          )} (${reportingDeliverable._config_.frequency})`}
        </Text>
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <DeleteArrayPropertyButton
            basePropertyKey={basePropertyKey}
            wipConfig={wipConfig}
            idx={idx}
          />
        )}
      </Flex>
      <Text fontWeight="bold" fontSize="18px" mt="10px" mb="10px">
        Metadata
      </Text>
      <Table borderWidth="1px" mb="35px">
        <Thead>
          <Tr>
            {_.map(metadataHeaders, (header) => (
              <Th key={header}>{commonUtils.toTitleCase(header)}</Th>
            ))}
            {reportingDeliverable.value === ('file' as const) && (
              <Th>File Types</Th>
            )}
            {wipConfig && <Th key="action">Action</Th>}
          </Tr>
        </Thead>
        <Tbody>
          <Tr>
            <Td>{name}</Td>
            <Td>{description}</Td>
            <Td>{section}</Td>
            <Td>{deliverableType}</Td>
            <Td>{frequency}</Td>
            <Td>
              {_.map(
                responsibleParties,
                ({
                  entity: {
                    name: responsiblePartyName,
                    slug: responsiblePartySlug,
                  },
                }) => (
                  <Text key={`responsibleParty-${responsiblePartySlug}`}>
                    {responsiblePartyName}
                  </Text>
                ),
              )}
            </Td>
            <Td>{getBooleanSymbol(isWrittenIn)}</Td>
            {reportingDeliverable.value === ('file' as const) && (
              <Td>
                <Text>
                  {_.isEmpty(reportingDeliverable.fileTypes)
                    ? 'None'
                    : _.join(reportingDeliverable.fileTypes, ', ')}
                </Text>
              </Td>
            )}
            {wipConfig?.isMutateAllowed(fullPropertyKey) && (
              <Td>
                <EditButton setIsOpen={setEditIsOpen} />
                <EditReportingDeliverableModal
                  isOpen={editIsOpen}
                  setIsOpen={setEditIsOpen}
                  wipConfig={wipConfig}
                  basePropertyKey={fullPropertyKey}
                  reportingDeliverable={reportingDeliverable}
                  onSubmit={(
                    updatedDeliverable: CreditAgreement.DomainEntity.DEPRECATED.DeliverableProperty,
                  ) =>
                    wipConfig.formProps.setFieldValue(
                      fullPropertyKey,
                      updatedDeliverable,
                    )
                  }
                />
              </Td>
            )}
          </Tr>
        </Tbody>
      </Table>

      <Box mb="35px">
        <Text fontWeight="bold" fontSize="18px" mt="10px" mb="10px">
          Dependencies
        </Text>
        {_.isEmpty(dependencies) && (
          <Text>No dependencies have been configured for this deliverable</Text>
        )}
        {!_.isEmpty(dependencies) && (
          <List>
            {_.map(dependencies, (dependency) => (
              <ListItem key={dependency}>
                <Link href={`#${_.kebabCase(dependency)}`}>
                  {_.startCase(dependency)}
                </Link>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      <Box mb="35px">
        <Flex>
          <Text fontWeight="bold" fontSize="18px" mt="10px" mb="10px">
            Party Permissions
          </Text>
          {wipConfig?.isMutateAllowed(
            `${fullPropertyKey}._config_.partyPermissions`,
          ) && (
            <>
              <AddIconButton
                ariaLabel="add permissions"
                label="Add permissions"
                onClick={() => setIsEditPermissionOpen(true)}
              />
              <EditReportingDeliverablesPermissionsModal
                deliverableName={name}
                wipConfig={wipConfig}
                isOpen={isEditPermissionOpen}
                partyPermissions={partyPermissions}
                setIsOpen={setIsEditPermissionOpen}
                onSubmit={(newPermissions) => {
                  wipConfig.formProps.setFieldValue(
                    `${fullPropertyKey}._config_.partyPermissions`,
                    newPermissions,
                  );
                }}
              />
            </>
          )}
        </Flex>
        {_.isEmpty(partyPermissions) && (
          <Text>No parties have had permissions configured</Text>
        )}
        {!_.isEmpty(partyPermissions) && (
          <Table borderWidth="1px">
            <Thead>
              <Tr>
                <Th>Organization</Th>
                <Th textAlign="center">
                  <Text display="inline-block">Viewing</Text>
                  <Tooltip label="Gives organization ability to view the deliverables detail page without dependencies section">
                    <Icon ml="8px" fontSize="18px" display="inline-block">
                      <FiInfo />
                    </Icon>
                  </Tooltip>
                </Th>
                <Th textAlign="center">
                  <Text display="inline-block">Download</Text>
                  <Tooltip label="Gives organization ability to download the current submission of a deliverable">
                    <Icon ml="8px" fontSize="18px" display="inline-block">
                      <FiInfo />
                    </Icon>
                  </Tooltip>
                </Th>
                {wipConfig && <Th>Edit/Delete</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {_.map(partyPermissions, (permission, permissionIdx: number) => {
                const { entity, permissionTypes } = permission;
                const canView = _.includes(permissionTypes, 'view');
                const canDownload = _.includes(permissionTypes, 'download');
                return (
                  <PermissionRow
                    key={entity.id}
                    canView={canView}
                    canDownload={canDownload}
                    entity={entity}
                    partyPermissions={partyPermissions}
                    wipConfig={wipConfig}
                    permissionIdx={permissionIdx}
                    deliverableName={name}
                    deliverablePermission={permission}
                    fullPropertyKey={fullPropertyKey}
                  />
                );
              })}
            </Tbody>
          </Table>
        )}
      </Box>
      <Box mb="35px">
        <Text fontWeight="bold" fontSize="18px" mt="10px" mb="10px">
          Notifications
        </Text>
        {_.isEmpty(preUploadNotificationProcesses) &&
        _.isEmpty(postUploadNotificationProcesses) ? (
          <Text>No notifications have been configured</Text>
        ) : (
          <Table borderWidth="1px">
            <Thead>
              <Tr>
                <Th>Event</Th>
                <Th textAlign="center">Notification</Th>
              </Tr>
            </Thead>
            <Tbody>
              {_.concat(
                _.map(preUploadNotificationProcesses, (notification) => {
                  const { notificationType } = notification;

                  return (
                    <Tr key={`${notificationType}-mediumPlaceholder-pre`}>
                      <Td>Before Upload</Td>
                      <Td textAlign="center">
                        {commonUtils.toSentenceCase(notificationType)}
                      </Td>
                    </Tr>
                  );
                }),
                _.map(postUploadNotificationProcesses, (notification) => {
                  const { notificationType } = notification;

                  return (
                    <Tr key={`${notificationType}-mediumPlaceholder-post`}>
                      <Td>On Upload</Td>
                      <Td>{commonUtils.toSentenceCase(notificationType)}</Td>
                    </Tr>
                  );
                }),
              )}
            </Tbody>
          </Table>
        )}
      </Box>
      <Box mb="35px">
        <Flex alignItems="center" mb="20px">
          <Text fontWeight="bold" fontSize="18px">
            Due Dates
          </Text>
          {wipConfig?.isMutateAllowed(fullPropertyKey) && (
            <>
              <AddIconButton
                label="Add due dates"
                ariaLabel="add due dates"
                onClick={() => setUploadDueDatesIsOpen(true)}
              />
              <AddDeliverableDueDateModal
                basePropertyKey={fullPropertyKey}
                isOpen={uploadDueDatesIsOpen}
                setIsOpen={setUploadDueDatesIsOpen}
                wipConfig={wipConfig}
              />
            </>
          )}
          {!_.isEmpty(reportingDeliverable.dueDates) && (
            <CsvDownloadButton
              dataRows={_.map(reportingDeliverable.dueDates, (dueDate) => ({
                dueDate,
              }))}
              headers={[{ label: 'dueDate', key: 'dueDate' }]}
              // @jendevelops TODO: namespace file with creditFacilitySlug
              filenamePrefix={`${_.kebabCase(
                reportingDeliverable.name,
              )}-dueDates`}
            />
          )}
        </Flex>
        {_.isEmpty(reportingDeliverable.dueDates) ? (
          <Alert status="error">
            <AlertIcon />
            <AlertDescription>
              No due dates configured for deliverable
            </AlertDescription>
          </Alert>
        ) : (
          <Alert status="success">
            <AlertIcon />
            <AlertDescription>{`${
              reportingDeliverable.dueDates.length
            } due date${
              reportingDeliverable.dueDates.length !== 1 ? 's' : ''
            } configured`}</AlertDescription>
          </Alert>
        )}
      </Box>
    </Box>
  );
};

type ReportingDeliverablesDEPRECATEDProps = {
  reportingDeliverables: CreditAgreement.DomainEntity.DEPRECATED.DeliverablePropertyWithEntities[];
  // creditFacilityId: string;
  wipConfig?: WipConfig;
};
export const ReportingDeliverablesDEPRECATED = ({
  reportingDeliverables,
  wipConfig,
}: ReportingDeliverablesDEPRECATEDProps) => {
  const [isAddDeliverableOpen, setIsAddDeliverableOpen] = useState(false);
  // const { data, loading } = useGetCreditFacilityQuery({
  //   variables: { creditFacilityId },
  // });
  const basePropertyKey = 'reportingDeliverables';
  // const toast = useToast();

  // if (!data || loading) {
  //   return <Skeleton id="reporting-deliverables-loading-credit-facility" />;
  // }

  // const { slug: creditFacilitySlug } = data.creditFacility;

  return (
    <Box mt="40px">
      <StickyHeaderSection
        labelText="Reporting Deliverables"
        labelRightComponent={
          wipConfig?.isMutateAllowed(basePropertyKey) ? (
            <>
              <AddIconButton
                ariaLabel="add new deliverable"
                label="Add new deliverable"
                onClick={() => setIsAddDeliverableOpen(true)}
              />
              <EditReportingDeliverableModal
                isOpen={isAddDeliverableOpen}
                setIsOpen={setIsAddDeliverableOpen}
                wipConfig={wipConfig}
                basePropertyKey={basePropertyKey}
                onSubmit={(
                  newDeliverable: CreditAgreement.DomainEntity.DEPRECATED.DeliverableProperty,
                ) => {
                  wipConfig.formProps.setFieldValue(basePropertyKey, [
                    newDeliverable,
                    ...reportingDeliverables,
                  ]);
                }}
              />
            </>
          ) : null
        }
      >
        {!_.isEmpty(reportingDeliverables) ? (
          _.map(reportingDeliverables, (reportingDeliverable, idx) => {
            const id = getIterationKey(reportingDeliverable);
            return (
              <ReportingDeliverable
                key={id}
                basePropertyKey={basePropertyKey}
                idx={idx}
                reportingDeliverable={reportingDeliverable}
                // creditFacilitySlug={creditFacilitySlug}
                wipConfig={wipConfig}
              />
            );
          })
        ) : (
          <Alert>
            <AlertIcon />
            <AlertDescription>
              There are no reporting deliverables configured
            </AlertDescription>
          </Alert>
        )}
      </StickyHeaderSection>
    </Box>
  );
};
